import { useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Text,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Persona,
  PersonaSize,
  Link,
  Spinner,
  MessageBar,
  MessageBarType,
  Separator,
  TextField,
  getTheme,
  ActionButton,
  Depths,
} from '@fluentui/react';
import MetaTable from '../components/MetaTable';

/**
 * ProposalDashboard
 * --------------------------------------
 * Props:
 *   - proposalId (string | number) — required
 *
 * Expected API (component is resilient; it will try these in order):
 *   1) GET /api/proposalDashboard/:id/dashboard => {
 *        proposal, client, staff, projects,
 *        company_descriptions: [...], approach_descriptions: [...]
 *      }
 *   2) Fallback endpoints if #1 doesn't exist:
 *      GET /api/proposalDashboard/:id
 *      GET /api/proposalDashboard/:id/staff
 *      GET /api/proposalDashboard/:id/projects
 *      GET /api/proposalDashboard/:id/descriptions/company
 *      GET /api/proposalDashboard/:id/descriptions/approach
 *      GET /api/clients/:clientId
 */

export const styles = {
  root: { padding: 16, maxWidth: 1200, margin: '24px auto' },
  headerRow: { alignItems: 'center' },
  title: { fontSize: 24, fontWeight: 600 },
  subTitle: { color: '#666' },
  sectionCard: {
    background: 'white',
    border: '1px solid #e1dfdd',
    borderRadius: 8,
    padding: 16,
    boxShadow: Depths.depth8
  },
  pillRow: { flexWrap: 'wrap' },
  pill: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    background: '#f3f2f1',
    marginRight: 8,
    marginBottom: 8,
    fontSize: 12,
  },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' },
  wrapText: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', lineHeight: 1.45 },
};

export default function ProposalDashboard(props) {
  const { proposalId } = props.match.params;
  const [data, setData] = useState({
    proposal: null,
    client: null,
    staff: [],
    projects: [],
    company_descriptions: [],
    approach_descriptions: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      setLoading(true); setError(null);
      try {
        const dash = await tryGet(`/api/proposalDashboard/${proposalId}/dashboard`);
        if (dash && dash.proposal) {
          if (!cancelled) setData(normalizeDashboard(dash));
          return;
        }
        const proposal = await mustGet(`/api/proposalDashboard/${proposalId}`);
        let client = null;
        if (proposal && (proposal.client_id || proposal.clientId)) {
          const cid = proposal.client_id ?? proposal.clientId;
          client = await tryGet(`/api/clients/${cid}`);
        }
        const staff = (await tryGet(`/api/proposalDashboard/${proposalId}/staff`)) || [];
        const projects = (await tryGet(`/api/proposalDashboard/${proposalId}/projects`)) || [];
        const company_descriptions = (await tryGet(`/api/proposalDashboard/${proposalId}/descriptions/company`)) || [];
        const approach_descriptions = (await tryGet(`/api/proposalDashboard/${proposalId}/descriptions/approach`)) || [];
        const tags = (await tryGet(`/api/proposalDashboard/${proposalId}/tags`)) || [];
        if (!cancelled) setData(normalizeDashboard({ proposal, client, staff, projects, company_descriptions, approach_descriptions, tags }));
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, [proposalId]);

  const { proposal, client, staff, projects, tags } = data;

  const title = useMemo(() => pickFirst(proposal, ['title', 'name']) || `Proposal ${proposalId}` , [proposalId, proposal]);
  const year = useMemo(() => pickFirst(proposal, ['year', 'date_submitted_year']) || extractYear(pickFirst(proposal, ['date_submitted'])), [proposal]);
  const proposalType = useMemo(() => pickFirst(proposal, ['proposal_type', 'type']), [proposal]);
  const budget = useMemo(() => pickFirst(proposal, ['budget', 'total_budget']), [proposal]);
  const summary = useMemo(() => pickFirst(proposal, ['canonical_summary', 'summary']), [proposal]);
  const about = useMemo(() => pickFirst(proposal, ['about']), [proposal]);
  const workplan = useMemo(() => pickFirst(proposal, ['workplan']), [proposal]);
  const approach = useMemo(() => pickFirst(proposal, ['approach']), [proposal]);

  let sharepointUrl = proposal && proposal.sharepoint_url && proposal.sharepoint_url.replaceAll('%20', ' ').replaceAll('%26', '&');
  
  let baseUrl = 'https://compassrm.sharepoint.com/_layouts/15/sharepoint.aspx?q=';
  if(sharepointUrl){
    let filePath = sharepointUrl.split('/');
    if(filePath.length){
      sharepointUrl = baseUrl += filePath[filePath.length - 1] + `&v=search`;
    }
  }

  const projectColumns = [
    { key: 'col1', name: 'Title', fieldName: 'title', minWidth: 220, isResizable: true, onRender: (item) => (
      item.id ? <Link href={`/projects/${item.id}`}>{item.title}</Link> : <span>{item.title}</span>
    ) },
    { key: 'col2', name: 'Client', fieldName: 'client_name', minWidth: 120, isResizable: true },
    { key: 'col3', name: 'Year', fieldName: 'year', minWidth: 60, maxWidth: 80, isResizable: true },
  ];

  if (loading) return <Spinner label="Loading proposal..." />;
  if (error) return <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>;
  if (!proposal) return <MessageBar>Proposal not found.</MessageBar>;

  return (
    <Stack styles={{ root: styles.root }} tokens={{ childrenGap: 16 }}>
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center" styles={{ root: styles.headerRow }}>
        <Stack>
          <Text styles={{ root: styles.title }}>{title}</Text>
          <Text styles={{ root: styles.subTitle }}>
            {client?.name ? `${client.name}` : ''}
            {year ? (client?.name ? ` • ${year}` : year) : null}
            {proposalType ? ` • ${proposalType}` : ''}
          </Text>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          {sharepointUrl ? (
            <ActionButton
              text="Open in SharePoint" 
              iconProps={{ iconName: 'SharepointAppIcon16' }} 
              title="Open SharePoint" 
              ariaLabel="Open SharePoint" 
              onClick={() => window.open(sharepointUrl, '_blank', 'noreferrer')} />
          ) : null}
          <ActionButton
              text="Edit" 
              iconProps={{ iconName: 'Edit' }} 
              title="Edit Proposal" 
              ariaLabel="Edit Proposal" 
              onClick={() => window.open(`${props.match.url}/edit`, '_self', 'noreferrer')} />
        </Stack>
      </Stack>

      {/* Summary & Meta */}
      <Stack tokens={{ childrenGap: 16 }}>
        <Stack.Item grow={1} styles={{root: styles.sectionCard}}>
          <Text variant="xLarge">Summary</Text>
          <Separator />
          <ExpandableText text={summary || '—'} />
        </Stack.Item>

        <Stack.Item grow={1} styles={{root: styles.sectionCard}}>
          <Text variant="xLarge">At a glance</Text>
          <Separator />
          <MetaTable 
            labelColWidth={120} 
            borderless
            items={[
              {label: "Year", text: year},
              {label: "Tags", text: tags.map(t => t.name).join(", "), onRender: (item) => (
                <Stack horizontal wrap tokens={{childrenGap: 4}}>
                  {tags.map(t => (
                    <Stack.Item key={t.name}>
                      <Chip>{t.name}</Chip>
                    </Stack.Item>
                  ))}
                </Stack>)},
              {label: 'Client', text: client?.name || '-'}, 
              {label: "Type", text: proposalType || '-'}, 
              // {label: "Budget", text: budget || "-"}, 
              {label: "Project #", text: proposal.project_number}]} />
        </Stack.Item>
      </Stack>

      <Stack grow styles={{ root: styles.sectionCard }}>
        <Text variant="xLarge">About</Text>
        <Separator />
        <ExpandableText text={about || '—'} />
      </Stack>
      
      <Stack grow styles={{ root: styles.sectionCard }}>
        <Text variant="xLarge">Approach</Text>
        <Separator />
        <ExpandableText text={approach || '—'} />
      </Stack>
      {/* </Stack> */}

      <Stack styles={{ root: styles.sectionCard }}>
        <Text variant="xLarge">Workplan</Text>
        <Separator />
        <ExpandableText text={workplan || '—'} />
      </Stack>

      {/* Budget */}
      <Stack styles={{ root: styles.sectionCard }}>
        <Text variant="large" styles={{ root: { fontWeight: 600 } }}>Budget</Text>
        <Separator />
        <ExpandableText text={budget || '—'} />
      </Stack>

      {/* Staff */}
      <Stack styles={{ root: styles.sectionCard }} tokens={{ childrenGap: 8 }}>
        <Stack>
          <Text variant="large" styles={{ root: { fontWeight: 600 } }}>Team</Text>
          <Separator />
        </Stack>
        {staff?.length ? (
          <Stack tokens={{ childrenGap: 12 }}>
            {staff.map((p) => (
              <div key={p.id}>
                <Persona
                  text={p.name || `Person: ${p.id}`}
                  onClick={() => props.history.push(`/people/${p.person_id}`)}
                  secondaryText={p.role || p.title || ''} 
                  size={PersonaSize.size40} />
                <ExpandableText text={p.bio_text || '—'} />
              </div>
            ))}
          </Stack>
        ) : (
          <Text>—</Text>
        )}
      </Stack>

      {/* Linked Projects */}
      <Stack styles={{ root: styles.sectionCard }} tokens={{ childrenGap: 8 }}>
        <Stack>
          <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>Related Projects</Text>
          <Separator />
        </Stack>
        {projects?.length ? (
          <DetailsList
            items={projects}
            columns={projectColumns}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
            styles={{ root: { overflowX: 'auto' } }}
          />
        ) : (
          <Text>—</Text>
        )}
      </Stack>
    </Stack>
  );
}

export const Chip = ({onClick=() => null, primary, children, variant, size=14, style}) => {
  
  const chipStyle = useMemo(() => {

    if(variant){
      return {
      background: variant === "Open" ? 
        `${getTheme().palette.green}20` : 
        getTheme().palette.neutralLighterAlt,
      borderRadius: 16, 
      padding: `2px 8px`, 
      border: `1px solid ${variant === "Open" ? getTheme().palette.green : getTheme().palette.neutralLight}`, 
      cursor: 'pointer', 
      color: variant === 'Open' ? getTheme().palette.greenDark : 'inherit',
      fontSize: size,
      selectors: {
        '&:hover': {
          boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)',
        },
      },
      ...style
    }
    }

    return {
      background: primary ? 
        getTheme().palette.themePrimary : 
        getTheme().palette.neutralLighterAlt,
      borderRadius: 16, 
      padding: `2px 8px`, 
      border: `1px solid ${getTheme().palette.neutralLight}`, 
      cursor: 'pointer', 
      color: primary ? 'white' : 'inherit',
      fontSize: size,
      selectors: {
        '&:hover': {
          boxShadow: Depths.depth8,
          transform: 'translateY(-2px)'
        },
      },
      ...style
    }
  }, [primary, variant, size, style]);

  return <Text 
    styles={{root: chipStyle}} 
    onClick={onClick} >
      {children}
  </Text>
}

export function ExpandableText({ text, maxChars = 800 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <Text>—</Text>;
  const str = String(text);
  const needsTrim = str.length > maxChars && !expanded;
  const shown = needsTrim ? str.slice(0, maxChars) + '…' : str;
  return (
    <div>
      {/* <p>{shown}</p> */}
      <TextField styles={{field: {padding: 0}}} multiline value={shown} autoAdjustHeight readOnly borderless />
      {needsTrim && (
        <Link onClick={() => setExpanded(true)} styles={{ root: { fontSize: 14, marginTop: 6, display: 'inline-block' } }}>Show more</Link>
      )}
    </div>
  );
}

// ---------------- Helpers ----------------
async function tryGet(url) {
  try { const r = await fetch(url); if (!r.ok) return null; return await r.json(); } catch { return null; }
}
async function mustGet(url) {
  const r = await fetch(url); if (!r.ok) throw new Error(`${r.status} ${r.statusText} — ${url}`); return r.json();
}

function normalizeDashboard(input) {
  const proposal = input?.proposal || input;
  const client = input?.client || null;
  const staff = Array.isArray(input?.staff) ? input.staff : [];
  const projects = Array.isArray(input?.projects) ? input.projects : [];
  const company_descriptions = Array.isArray(input?.company_descriptions) ? input.company_descriptions : [];
  const approach_descriptions = Array.isArray(input?.approach_descriptions) ? input.approach_descriptions : [];
  const tags = input.tags || [];

  const normProjects = projects.map((p) => ({
    id: p.id,
    title: pickFirst(p, ['title', 'name', 'project_number']) || `(Project ${p.id})`,
    client_name: p.client_name || p.client || null,
    year: p.year || extractYear(p.summary_end_date) || extractYear(p.summary_start_date),
  }));

  return { proposal, client, staff, projects: normProjects, company_descriptions, approach_descriptions, tags };
}

function pickFirst(obj, keys) {
  if (!obj) return undefined;
  for (const k of keys) { if (obj[k] != null && obj[k] !== '') return obj[k]; }
  return undefined;
}

function extractYear(v) {
  if (!v) return undefined; const d = new Date(v); return isNaN(d) ? undefined : d.getFullYear();
}


